import type { GetStaticPaths, GetStaticProps } from 'next'
import { useRouter } from 'next/router'
import type { FC, ReactNode } from 'react'
import React from 'react'

import type { PageProps } from '@motif/shared/pages/generate-static-props'
import { generateStaticProps } from '@motif/shared/pages/generate-static-props'
import { getActivePublicationTimestamp } from '@motif/shared/pages/queries/publications'
import SlugComponent from '@motif/shared/pages/slug'
import type { TemplateProps } from '@motif/shared/pages/template'
import { getTemplate } from '@motif/shared/pages/template'
import { getSlugComponents } from '@motif/shared/utils/routes'

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const slug = (params?.slug as string[]) || ['']
  if (
    slug?.length === 1 &&
    (slug[0] === 'sw.js' || /^workbox-.+\.js$/.test(slug[0]))
  ) {
    // next-pwa is not stable, and in some combinations of a new
    // Next.js version and a new next-pwa version (e.g. 12.2.3 and
    // 5.5.5) sw.js and workbox-*.js files do not get generated
    // in the public folder, resulting in the requests being
    // passed on to here, which will result in a "file not found",
    // which would result in the build cache being invalidated.
    // This results in unecessary and expensing db calls.
    return {
      notFound: true,
    }
  }

  const projectId = process.env.MOTIF_WEBSITE_PROJECT_ID || ''
  const timestamp = await getActivePublicationTimestamp(projectId)
  const pageProps = await generateStaticProps(
    slug,
    projectId,
    timestamp,
    false,
    false,
    'motif.land',
    true
  )

  return {
    ...pageProps,
    revalidate: 10,
  }
  return {
    notFound: true,
  }
}

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: [],
    fallback: true,
  }
}

const Slug: FC<PageProps> & {
  isPublic: boolean
  getTemplate: (page: ReactNode, templateProps: TemplateProps) => JSX.Element
} = (props) => {
  const router = useRouter()
  const slugComponents = getSlugComponents(router)

  // We enable notifyResize for project subdomains (not custom domains).
  // Ideally, we would want to be able to set this flag via query params
  // so that it's not enabled by default, but this is not possible because
  // of SSR, and in practice this is when we need it (loading listing pages).
  return (
    <SlugComponent {...props} notifyResize slugComponents={slugComponents} />
  )
}

Slug.isPublic = true
Slug.getTemplate = getTemplate

export default Slug
